/**
 * PickProgressHeader - Live pick progress display
 *
 * Shows "12/45 picked" count with progress bar.
 * Updates in real-time as items are scanned.
 *
 * UX: Large text for warehouse visibility, tabular nums for alignment.
 */
import { Progress } from '@/components/ui/progress';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PickProgressHeaderProps {
  pickedCount: number;
  totalCount: number;
  className?: string;
}

export function PickProgressHeader({
  pickedCount,
  totalCount,
  className,
}: PickProgressHeaderProps) {
  const { t } = useTranslation('production');
  const percent = totalCount > 0 ? Math.round((pickedCount / totalCount) * 100) : 0;
  const isComplete = pickedCount === totalCount && totalCount > 0;

  return (
    <div
      className={cn('flex items-center gap-4 p-4', className)}
      role="status"
      aria-live="polite"
      aria-label={`Picking progress: ${pickedCount} of ${totalCount} items picked, ${percent}% complete`}
    >
      <div className="flex items-center gap-2">
        {isComplete && (
          <Check
            className="h-6 w-6 text-green-600"
            aria-hidden="true"
          />
        )}
        <span className="text-xl font-semibold tabular-nums" aria-hidden="true">
          {pickedCount}/{totalCount}
        </span>
        <span className="text-muted-foreground text-lg" aria-hidden="true">{t('pickProgress.picked')}</span>
      </div>
      {/* h-4 progress bar for warehouse visibility */}
      <Progress
        value={percent}
        className="flex-1 h-4"
        aria-label={`${percent}% complete`}
      />
      <span
        className="text-base text-muted-foreground tabular-nums w-14 text-right font-medium"
        aria-hidden="true"
      >
        {percent}%
      </span>
      {/* Screen reader announcement for completion */}
      {isComplete && (
        <span className="sr-only">{t('pickProgress.allPicked')}</span>
      )}
    </div>
  );
}
